import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Building2, UserPlus, Phone, Mail } from "lucide-react";

/**
 * Composant de gestion des responsables externes d'un litige.
 * - external_suppliers : références aux fiches Fournisseur (coordonnées dynamiques)
 * - external_thirdparties : intervenants libres (avocat, expert, etc.)
 */
export default function DisputeExternalResponsibles({ value, onChange, suppliers }) {
  const externalSuppliers = value.external_suppliers || [];
  const externalThirdparties = value.external_thirdparties || [];

  const suppliersIndex = useMemo(() => {
    const idx = {};
    suppliers.forEach((s) => { idx[s.id] = s; });
    return idx;
  }, [suppliers]);

  const addSupplier = () => {
    onChange({
      ...value,
      external_suppliers: [...externalSuppliers, { supplier_id: "", role: "", comment: "" }],
    });
  };

  const updateSupplier = (idx, patch) => {
    const next = [...externalSuppliers];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...value, external_suppliers: next });
  };

  const removeSupplier = (idx) => {
    onChange({
      ...value,
      external_suppliers: externalSuppliers.filter((_, i) => i !== idx),
    });
  };

  const addThirdParty = () => {
    onChange({
      ...value,
      external_thirdparties: [
        ...externalThirdparties,
        { id: null, company: "", name: "", phone: "", email: "", role: "", comment: "" },
      ],
    });
  };

  const updateThirdParty = (idx, patch) => {
    const next = [...externalThirdparties];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...value, external_thirdparties: next });
  };

  const removeThirdParty = (idx) => {
    onChange({
      ...value,
      external_thirdparties: externalThirdparties.filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="space-y-5 border-t pt-4">
      {/* === Responsables externes — Fournisseurs === */}
      <div className="space-y-2.5" data-testid="external-suppliers-section">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-primary" />
              Responsables externes (Fournisseurs)
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Coordonnées chargées dynamiquement depuis la fiche fournisseur.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addSupplier} data-testid="add-external-supplier-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
          </Button>
        </div>

        {externalSuppliers.length === 0 ? (
          <p className="text-xs italic text-muted-foreground py-2">Aucun responsable fournisseur ajouté.</p>
        ) : (
          <div className="space-y-2">
            {externalSuppliers.map((row, idx) => {
              const supplier = suppliersIndex[row.supplier_id];
              return (
                <div
                  key={idx}
                  className="border rounded-md p-3 bg-slate-50 space-y-2"
                  data-testid={`external-supplier-row-${idx}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                    <div className="md:col-span-5">
                      <Label className="text-xs">Fournisseur *</Label>
                      <Select
                        value={row.supplier_id || "_none"}
                        onValueChange={(v) => updateSupplier(idx, { supplier_id: v === "_none" ? "" : v })}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Sélectionner —</SelectItem>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.company_name || s.name || s.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-5">
                      <Label className="text-xs">Rôle dans le litige</Label>
                      <Input
                        className="h-9"
                        placeholder="Ex: Avocat, Expert, Assureur, Protection juridique…"
                        value={row.role || ""}
                        onChange={(e) => updateSupplier(idx, { role: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2 flex md:justify-end items-end h-full">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSupplier(idx)}
                        className="text-destructive h-9"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Coordonnées dynamiques (lecture seule) */}
                  {supplier && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground bg-white border rounded px-2 py-1.5">
                      {supplier.phone && (
                        <a href={`tel:${supplier.phone}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                          <Phone className="h-3 w-3" /> {supplier.phone}
                        </a>
                      )}
                      {supplier.email && (
                        <a href={`mailto:${supplier.email}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                          <Mail className="h-3 w-3" /> {supplier.email}
                        </a>
                      )}
                      {supplier.city && <span>· {supplier.city}</span>}
                      {!supplier.phone && !supplier.email && (
                        <span className="italic">Pas de coordonnées dans la fiche fournisseur.</span>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Commentaires</Label>
                    <Textarea
                      rows={2}
                      placeholder="Notes sur l'intervention de ce fournisseur dans le litige…"
                      value={row.comment || ""}
                      onChange={(e) => updateSupplier(idx, { comment: e.target.value })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === Tiers externes === */}
      <div className="space-y-2.5" data-testid="external-thirdparties-section">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <UserPlus className="h-4 w-4 text-primary" />
              Tiers externes (hors fournisseurs)
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Avocat, expert, protection juridique, assureur, etc. — saisie libre.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addThirdParty} data-testid="add-external-thirdparty-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
          </Button>
        </div>

        {externalThirdparties.length === 0 ? (
          <p className="text-xs italic text-muted-foreground py-2">Aucun tiers externe ajouté.</p>
        ) : (
          <div className="space-y-2">
            {externalThirdparties.map((row, idx) => (
              <div
                key={idx}
                className="border rounded-md p-3 bg-slate-50 space-y-2"
                data-testid={`external-thirdparty-row-${idx}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Société / Entité"
                    value={row.company || ""}
                    onChange={(e) => updateThirdParty(idx, { company: e.target.value })}
                  />
                  <Input
                    placeholder="Nom / Prénom"
                    value={row.name || ""}
                    onChange={(e) => updateThirdParty(idx, { name: e.target.value })}
                  />
                  <Input
                    placeholder="Téléphone"
                    value={row.phone || ""}
                    onChange={(e) => updateThirdParty(idx, { phone: e.target.value })}
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={row.email || ""}
                    onChange={(e) => updateThirdParty(idx, { email: e.target.value })}
                  />
                  <Input
                    className="md:col-span-2"
                    placeholder="Rôle (ex: Avocat, Expert, Protection juridique…)"
                    value={row.role || ""}
                    onChange={(e) => updateThirdParty(idx, { role: e.target.value })}
                  />
                </div>
                <Textarea
                  rows={2}
                  placeholder="Commentaires"
                  value={row.comment || ""}
                  onChange={(e) => updateThirdParty(idx, { comment: e.target.value })}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeThirdParty(idx)}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Retirer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
